// Client-side mirror of the backend GST validator (controllers/utils/gst.js on
// production). Kept byte-compatible in BEHAVIOUR with that file so the modal never
// offers a value the endpoint will reject, and never blocks one it would accept.
//
// IMPORTANT — Stores.gst_number is NOT an India-only field. Non-India orgs store VAT /
// sales-tax ids in it that share no format with a GSTIN, so validation is MARKET-GATED:
// the format + checksum rules apply only when the org is in India. An unconditional
// GSTIN check would reject every legitimate non-India tax id.
//
// This is deliberately a separate module from zoho_gst_match.js: that file's
// normalise_gst only trims and upper-cases, because it compares against the Zoho dump
// as Zoho holds it. Canonicalisation here additionally strips punctuation, which is the
// backend's persisted form. Merging the two would silently change dump matching.

// India GSTIN: 2-digit state code, 5-letter PAN prefix, 4 digits, PAN check letter,
// entity/registration char, literal 'Z', then the check digit.
const GST_FORMAT_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;

// The check-digit alphabet is positional: index in this string IS the digit value.
const CODES = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Uppercase and strip everything that is not alphanumeric, so a GSTIN pasted with
 * spaces or hyphens ("29 AAXCS2582B 1ZW") is compared in canonical form.
 * This matches the backend's normalise_gst, NOT the dump-comparison one next door.
 */
export const canonicalise_gst = (s) => (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

export const is_valid_gst_format = (s) => GST_FORMAT_REGEX.test(canonicalise_gst(s));

// GSTIN check-digit algorithm: weight alternates 1,2 across the first 14 chars; each
// weighted product contributes its quotient and remainder mod 36; the check char is the
// 36-complement of the running sum.
export const gst_checksum_ok = (s) => {
	const gst = canonicalise_gst(s);
	if (gst.length !== 15) return false;
	let sum = 0;
	for (let i = 0; i < 14; i++) {
		const code = CODES.indexOf(gst[i]);
		// A char outside the alphabet (indexOf === -1) is not a GSTIN character at all.
		if (code < 0) return false;
		const p = code * (i % 2 === 0 ? 1 : 2);
		sum += Math.floor(p / 36) + (p % 36);
	}
	return CODES[(36 - (sum % 36)) % 36] === gst[14];
};

// Accepts either a market name ('india') or a country code ('IN'), because callers have
// one or the other depending on which field of the store row they read.
export const is_india = (market) => {
	const m = (market === null || market === undefined) ? '' : String(market).trim().toLowerCase();
	return m === 'india' || m === 'in';
};

/**
 * Is `value` an acceptable tax id for this market?
 *  - India: a well-formed GSTIN with a correct check digit.
 *  - Everywhere else: any non-empty string.
 */
export const is_valid_gst = (value, { market } = {}) => {
	if (typeof value !== 'string') return false;
	const trimmed = value.trim();
	if (trimmed === '') return false;
	if (!is_india(market)) return true;
	return is_valid_gst_format(trimmed) && gst_checksum_ok(trimmed);
};

/**
 * The form a value should be PERSISTED in for this market — the frontend mirror of the
 * endpoint's `gst_to_persist`.
 *
 * India: the canonical GSTIN, punctuation and spacing stripped. is_valid_gst accepts a
 * punctuated GSTIN because it canonicalises before testing, so a caller that validates
 * one string and submits another will send a value the endpoint stores in a different
 * form than the one it was handed. Canonicalising at the point of submission keeps the
 * value the user approved, the value we send, and the value that lands identical.
 *
 * Everywhere else: returned trimmed only. gst_number holds VAT / sales-tax ids outside
 * India, where punctuation and case are part of the identifier and stripping them would
 * corrupt a legitimate value.
 */
export const canonicalise_for_market = (value, { market } = {}) => {
	const trimmed = (value === null || value === undefined) ? '' : String(value).trim();
	return is_india(market) ? canonicalise_gst(trimmed) : trimmed;
};

/**
 * The market to validate a store against.
 *
 * Deliberately reads `country` and NOT `Stores.market`, because that is exactly what
 * csp/finalise_org_gst does: `config.country_to_market(store.country)`. It never looks
 * at the market column. `Stores.market` is a free-form STRING(100) carrying values like
 * 'international' and 'USA' that is_india would not recognise, so preferring it would
 * let the frontend accept a value the endpoint then rejects with INVALID_GST_NUMBER —
 * or, worse, skip the GSTIN check for an Indian org whose market column says something
 * unexpected. Reading the same field as the backend keeps the two verdicts identical.
 */
export const market_of_store = (store_info) => {
	if (!store_info) return null;
	return store_info.country || null;
};
